import React, {useState} from "react";
import {CalculateVatBookTypeEnum, VatBookList, VatBookRestControllerApiFactory} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import {Box, Button, MenuItem, Select, TableCell, TextField, Typography} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import {btnSx, SoftChip} from "../../../../components/kit";
import {exportVatRegisterIssuedXls, exportVatRegisterReceivedXls} from "../../../../components/utils/xlsExport";
import {numSx, taxStatus} from "../tax_submission/labels";
import {useApiManager} from "../../../../components/utils/api";
import {fmtAmt} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";
import {FormButtonProps} from "../../../../components/form/form_inputs/form_button";

export const VatBookListPage = () => {
    const apiManager = useApiManager();
    const [calculating, setCalculating] = useState(false);
    const [trigger, setTrigger] = useState(0);

    const handleCalculate = (year: number, quarter: number, type: CalculateVatBookTypeEnum) => {
        const ctrl = apiManager.factory(VatBookRestControllerApiFactory);
        setCalculating(true);
        apiManager.execute(ctrl.calculateVatBook(year, quarter, type), `Libro IVA ${type} ${year}T${quarter} calculado`)
            .then(() => setTrigger(t => t + 1))
            .finally(() => setCalculating(false));
    };

    const currentYear = new Date().getFullYear();
    const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);

    // Libros registro factura a factura (#14730): por defecto, el último trimestre cerrado
    const [regYear, setRegYear] = useState(currentQuarter === 1 ? currentYear - 1 : currentYear);
    const [regQuarter, setRegQuarter] = useState(currentQuarter === 1 ? 4 : currentQuarter - 1);
    const [exporting, setExporting] = useState<"issued" | "received" | null>(null);

    const handleExport = (kind: "issued" | "received") => {
        const ctrl = apiManager.factory(VatBookRestControllerApiFactory);
        setExporting(kind);
        const call = kind === "issued"
            ? apiManager.execute(ctrl.issuedRegisterVatBook(regYear, regQuarter)).then(exportVatRegisterIssuedXls)
            : apiManager.execute(ctrl.receivedRegisterVatBook(regYear, regQuarter)).then(exportVatRegisterReceivedXls);
        call.finally(() => setExporting(null));
    };

    const listConfig: AppListConfig<VatBookList> = {
        title: "Libros de IVA",
        onClick: "/app/admin/accounting/vat-book",
        headers: [
            {title: "Año", width: 60},
            {title: "Trimestre", width: 80},
            {title: "Tipo", width: 100},
            {title: "Estado", width: 110},
            {title: "Base total", align: "right", width: 120},
            {title: "IVA total", align: "right", width: 110},
            {title: "Resultado neto", align: "right", width: 120},
            {title: "F. presentación", width: 120},
        ],
        actions: [
            {
                label: calculating ? "Calculando..." : `Calc. IVA emitidas ${currentYear}T${currentQuarter}`,
                action: () => handleCalculate(currentYear, currentQuarter, CalculateVatBookTypeEnum.EMITIDAS),
            } as FormButtonProps,
            {
                label: calculating ? "Calculando..." : `Calc. IVA recibidas ${currentYear}T${currentQuarter}`,
                action: () => handleCalculate(currentYear, currentQuarter, CalculateVatBookTypeEnum.RECIBIDAS),
            } as FormButtonProps,
        ],
        toolbar: (
            <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">Libros registro del trimestre:</Typography>
                <TextField
                    label="Año"
                    type="number"
                    size="small"
                    value={regYear}
                    onChange={e => setRegYear(parseInt(e.target.value) || currentYear)}
                    sx={{width: 100}}
                />
                <Select
                    size="small"
                    value={regQuarter}
                    onChange={e => setRegQuarter(Number(e.target.value))}
                    sx={{minWidth: 80, borderRadius: 2}}
                >
                    <MenuItem value={1}>T1</MenuItem>
                    <MenuItem value={2}>T2</MenuItem>
                    <MenuItem value={3}>T3</MenuItem>
                    <MenuItem value={4}>T4</MenuItem>
                </Select>
                <Button variant="outlined" size="small" sx={btnSx} startIcon={<DownloadRoundedIcon/>}
                        disabled={exporting !== null} onClick={() => handleExport("issued")}>
                    {exporting === "issued" ? "Generando..." : "Libro de expedidas (Excel)"}
                </Button>
                <Button variant="outlined" size="small" sx={btnSx} startIcon={<DownloadRoundedIcon/>}
                        disabled={exporting !== null} onClick={() => handleExport("received")}>
                    {exporting === "received" ? "Generando..." : "Libro de recibidas (Excel)"}
                </Button>
            </Box>
        ),
        decorator: (bean: VatBookList) => (
            <>
                <TableCell>{bean.year}</TableCell>
                <TableCell>T{bean.quarter}</TableCell>
                <TableCell>
                    <SoftChip label={bean.bookType === "EMITIDAS" ? "Emitidas" : "Recibidas"}
                              tone={bean.bookType === "EMITIDAS" ? "success" : "info"}/>
                </TableCell>
                <TableCell>
                    <SoftChip label={taxStatus(bean.status).label} tone={taxStatus(bean.status).tone}/>
                </TableCell>
                <TableCell align="right" sx={numSx}>{fmtAmt(bean.totalBase)}</TableCell>
                <TableCell align="right" sx={numSx}>{fmtAmt(bean.totalVat)}</TableCell>
                <TableCell align="right" sx={{...numSx, fontWeight: 700}}>{fmtAmt(bean.netResult)}</TableCell>
                <TableCell>{dateToString(bean.presentationDate) || "-"}</TableCell>
            </>
        ),
        supplier: async (currentPage, size, term, showDismissed) => {
            const ctrl = apiManager.factory(VatBookRestControllerApiFactory);
            return ctrl.pageVatBook(currentPage, size, undefined, undefined, undefined, undefined, undefined, term, showDismissed);
        },
    };

    return <AppList config={listConfig} trigger={trigger}/>;
};
